import React, { useEffect, useState, useCallback } from 'react';
import { useAds } from '../../contexts/AdContext';
import { isNativeMobile } from '../../utils/platform';
import adService from '../../services/adService';
import './InterstitialAd.css';

/**
 * Issue #311: Interstitial Ad Component
 * - Displays full-screen ad once per day on first app open
 * - Respects user consent settings
 * - Supports both web (demo/AdSense) and mobile (AdMob)
 *
 * Issue #378: Suppress the interstitial entirely on the user's very first
 * login after account creation. The Login flow sets
 * `firstLoginInterstitialSuppressed` in sessionStorage when the backend
 * reports `is_first_login: true`; this component reads that flag and skips
 * showing without marking the ad as shown for the day, so the regular
 * once-per-day behaviour resumes from the next login onwards.
 */

const INTERSTITIAL_STORAGE_KEY = 'lastInterstitialShownAt';
const FIRST_LOGIN_SUPPRESS_KEY = 'firstLoginInterstitialSuppressed';

function InterstitialAd({ onClose }) {
  const { isInitialized, demoMode, consentGiven } = useAds();
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(5); // 5 second countdown before close button appears

  // Check if interstitial should be shown today
  const shouldShowToday = useCallback(() => {
    const lastShown = localStorage.getItem(INTERSTITIAL_STORAGE_KEY);
    if (!lastShown) return true;

    const lastShownDate = new Date(lastShown);
    const today = new Date();

    // Compare dates (ignoring time)
    return (
      lastShownDate.getFullYear() !== today.getFullYear() ||
      lastShownDate.getMonth() !== today.getMonth() ||
      lastShownDate.getDate() !== today.getDate()
    );
  }, []);

  // Mark interstitial as shown today
  const markAsShown = useCallback(() => {
    localStorage.setItem(INTERSTITIAL_STORAGE_KEY, new Date().toISOString());
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    setIsVisible(false);
    markAsShown();
    onClose?.();
  }, [markAsShown, onClose]);

  // Initialize and show interstitial
  useEffect(() => {
    if (!isInitialized) return;

    // Issue #378: Skip the interstitial for the user's first login after
    // account creation. The flag persists for the whole first session so
    // page reloads also skip; we deliberately do NOT mark the ad as shown
    // today, so the normal once-per-day cadence applies from the next login.
    let firstLoginSuppressed = false;
    try {
      firstLoginSuppressed = sessionStorage.getItem(FIRST_LOGIN_SUPPRESS_KEY) === 'true';
    } catch (e) {
      // sessionStorage unavailable - fall through to default behaviour.
    }
    if (firstLoginSuppressed) {
      onClose?.();
      return;
    }

    if (!shouldShowToday()) {
      onClose?.();
      return;
    }

    // For native mobile, try to show AdMob interstitial
    if (isNativeMobile() && !demoMode) {
      adService.showInterstitial()
        .then((shown) => {
          if (shown) {
            markAsShown();
            onClose?.();
          } else {
            // Fallback to demo mode if AdMob fails
            setIsVisible(true);
          }
        })
        .catch(() => {
          setIsVisible(true);
        });
    } else {
      // Web or demo mode - show custom interstitial
      setIsVisible(true);
    }
  }, [isInitialized, demoMode, shouldShowToday, markAsShown, onClose]);

  // Countdown timer
  useEffect(() => {
    if (!isVisible || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible, countdown]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && countdown <= 0) {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, countdown, handleClose]);

  if (!isVisible) return null;

  const isPersonalized = consentGiven === true;
  const adTypeLabel = isPersonalized ? 'Personalized Ad' : 'NPA Ad';
  const adColor = isPersonalized ? '#4CAF50' : '#FF9800';

  return (
    <div
      className="interstitial-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="interstitial-title"
    >
      <div className="interstitial-container">
        {/* Close button - shows countdown or X based on timer */}
        <button
          className="interstitial-close-button"
          onClick={countdown <= 0 ? handleClose : undefined}
          disabled={countdown > 0}
          aria-label={countdown <= 0 ? "Anzeige schliessen" : `Noch ${countdown} Sekunden`}
        >
          {countdown > 0 ? countdown : '\u00D7'}
        </button>

        {/* Ad content */}
        <div className="interstitial-content">
          <div className="interstitial-header">
            <span className="interstitial-label">Anzeige</span>
            <span
              className="interstitial-type-badge"
              style={{ backgroundColor: adColor }}
            >
              {adTypeLabel}
            </span>
          </div>

          <div className="interstitial-body">
            <div
              className="interstitial-icon"
              style={{ backgroundColor: adColor }}
              aria-hidden="true"
            >
              AD
            </div>
            <h2 id="interstitial-title" className="interstitial-headline">
              {isPersonalized
                ? 'Exklusive Angebote fur dich!'
                : 'Aktuelle Studenten-Angebote'}
            </h2>
            <p className="interstitial-description">
              {isPersonalized
                ? 'Entdecke personalisierte Deals basierend auf deinen Interessen.'
                : 'Allgemeine Anzeigen und Angebote fur Studierende.'}
            </p>
            <div className="interstitial-cta">
              <span style={{ color: adColor }}>Mehr erfahren</span>
            </div>
          </div>

          <div className="interstitial-footer">
            <span className="interstitial-advertiser">Sample Interstitial Ad</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InterstitialAd;
